/**
 * Auth session module — Firebase Authentication + backend user sync.
 *
 * Identity is owned by Firebase. The flow is:
 *   1. signInWithPopup() with a social provider  → Firebase ID token
 *   2. POST /auth/sync-user with that token       → local user row (roles/plan)
 *
 * The Firebase SDK persists the session and refreshes ID tokens automatically,
 * so this module keeps NO tokens of its own. `getIdToken()` always asks the
 * SDK for a fresh, valid token.
 */

import {
  signInWithPopup,
  signOut,
  type AuthProvider as FirebaseAuthProvider,
  type User as FirebaseUser,
} from 'firebase/auth';
import {
  auth,
  facebookProvider,
  googleProvider,
  microsoftProvider,
  type ProviderId,
} from '@/src/infrastructure/config/firebase';
import { CURRENT_POLICY_VERSION } from '@/src/infrastructure/config/policyVersion';

const POLICY_ACCEPTANCE_KEY = 'invitecloud.policy_acceptance.v1';

const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:4000') + '/v1';


/** The authenticated user as the app's UI consumes it. */
export type SessionUser = {
  id: string;
  email: string;
  displayName: string;
  username: string | null;
  phone: string | null;
  avatar: string | null;
  provider: string | null;
  role: string;
  plan: string;
  /** ISO timestamp of the row's last mutation; used to cache-bust avatars. */
  updatedAt: string | null;
};

/** Editable fields accepted by `PATCH /v1/auth/me`. */
export type UpdateProfilePayload = {
  name?: string;
  username?: string | null;
  email?: string;
  phone?: string | null;
  avatar?: string | null;
};

export type AuthResult = { ok: boolean; error?: string };

const PROVIDERS: Record<ProviderId, FirebaseAuthProvider> = {
  google: googleProvider,
  facebook: facebookProvider,
  microsoft: microsoftProvider,
};

/**
 * Map raw Firebase / network errors to messages safe to show a user.
 * Covers the cases called out in the auth spec (popup blocked/closed,
 * provider disabled, duplicate email, network error).
 */
export function describeAuthError(err: unknown): string {
  const code = (err as { code?: string })?.code ?? '';
  switch (code) {
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return 'Sign-in was cancelled. Please try again.';
    case 'auth/popup-blocked':
      return 'Your browser blocked the sign-in popup. Allow popups and retry.';
    case 'auth/account-exists-with-different-credential':
      return 'An account already exists with this email using a different provider.';
    case 'auth/operation-not-allowed':
      return 'This sign-in provider is not enabled. Contact support.';
    case 'auth/network-request-failed':
      return 'Network error. Check your connection and try again.';
    case 'auth/unauthorized-domain':
      return 'This domain is not authorized for sign-in.';
    default:
      if (err instanceof Error && err.message) return err.message;
      return 'Something went wrong during sign-in. Please try again.';
  }
}

/** Open the provider popup. Throws Firebase errors for the caller to map. */
export async function signInWithProvider(id: ProviderId): Promise<void> {
  await signInWithPopup(auth, PROVIDERS[id]);
}

/** Sign the user out of Firebase (clears persisted session). */
export async function logout(): Promise<void> {
  await signOut(auth);
}

/** A fresh, valid Firebase ID token, or null when signed out. */
export async function getIdToken(): Promise<string | null> {
  const current = auth.currentUser;
  if (!current) return null;
  return current.getIdToken();
}

/**
 * Upsert the signed-in Firebase user into the backend and return the local
 * profile (roles + plan live there, not in Firebase). Call this once after
 * every sign-in and on app load when a Firebase session is restored.
 */
export async function syncUser(fbUser: FirebaseUser): Promise<SessionUser> {
  const token = await fbUser.getIdToken();

  // Read local policy acceptance record to include in the sync payload.
  // The backend writes this to the audit_log as an immutable POLICY_ACCEPTED
  // record per IT Act §10A and DPDP Act 2023.
  let policyFields: {
    policyAcceptedAt?: string;
    policyVersion?: string;
    policyAcceptedVia?: string;
  } = {};
  try {
    const raw = localStorage.getItem(POLICY_ACCEPTANCE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as {
        acceptedAt?: string;
        policyVersion?: string;
        acceptedVia?: string;
      };
      // Only send if this matches the current policy version — prevents
      // re-sending stale acceptance records from an older policy revision.
      if (parsed.policyVersion === CURRENT_POLICY_VERSION && parsed.acceptedAt) {
        policyFields = {
          policyAcceptedAt: parsed.acceptedAt,
          policyVersion: parsed.policyVersion,
          policyAcceptedVia: parsed.acceptedVia,
        };
      }
    }
  } catch (_) { /* localStorage unavailable — degrade gracefully */ }

  const res = await fetch(`${API_BASE}/auth/sync-user`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      displayName: fbUser.displayName ?? undefined,
      avatar: fbUser.photoURL ?? undefined,
      provider: fbUser.providerData[0]?.providerId ?? undefined,
      ...policyFields,
    }),
  });

  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = Array.isArray(body?.message)
      ? body.message.join(', ')
      : body?.message ?? 'Failed to sync your account.';
    throw new Error(msg);
  }

  // Backend wraps responses: { success: true, data: {...} }
  return toSessionUser(body.data ?? body);
}

/** Map the backend `/auth/me|sync-user|PATCH me` payload to the UI shape. */
function toSessionUser(data: any): SessionUser {
  return {
    id: data.id,
    email: data.email,
    displayName: data.name,
    username: data.username ?? null,
    phone: data.phone ?? null,
    avatar: data.avatar ?? null,
    provider: data.provider ?? null,
    role: data.role,
    plan: data.plan,
    updatedAt: data.updatedAt ?? null,
  };
}

async function authedJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await auth.currentUser?.getIdToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers as Record<string, string> | undefined),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  const body = await res.json().catch(() => ({} as any));
  if (!res.ok) {
    const msg = Array.isArray(body?.message)
      ? body.message.join(', ')
      : body?.message ?? `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return (body?.data !== undefined ? body.data : body) as T;
}

/** Re-fetch the canonical profile from the backend. */
export async function fetchProfile(): Promise<SessionUser> {
  const data = await authedJson<any>('/auth/me');
  return toSessionUser(data);
}

// ── Phase 4 RBAC snapshot types ──────────────────────────────────────────────

export type AuthSnapshotUser = {
  id: string;
  email: string;
  primaryRole: string | null;
};

export type PartnerProfileSnapshot = {
  id: string;
  businessName: string;
  partnerType: string;
  status: string;
  onboardingStep: string;
  completedOnboarding: boolean;
  ownerName?: string | null;
  legalName?: string | null;
  phone?: string | null;
  websiteUrl?: string | null;
  googleMapsUrl?: string | null;
  logoUrl?: string | null;
};

export type OnboardingSnapshot = {
  step: string;
  completedOnboarding: boolean;
};

export type AuthSnapshot = {
  user: AuthSnapshotUser;
  roles: string[];
  permissions: string[];
  partnerProfile: PartnerProfileSnapshot | null;
  onboarding: OnboardingSnapshot | null;
  roleSelectionRequired: boolean;
};

/**
 * Fetch the full RBAC auth snapshot from GET /auth/me.
 * Returns roles, permissions, partnerProfile and onboarding — the single
 * source of truth for frontend permission hydration (Phase 4 rule).
 */
export async function fetchAuthSnapshot(): Promise<AuthSnapshot> {
  const data = await authedJson<any>('/auth/me');
  return {
    user: data.user ?? {
      id: data.id,
      email: data.email,
      primaryRole: data.primaryRole ?? null,
    },
    roles: data.roles ?? [],
    permissions: data.permissions ?? [],
    partnerProfile: data.partnerProfile ?? null,
    onboarding: data.onboarding ?? null,
    roleSelectionRequired: data.roleSelectionRequired ?? false,
  };
}

/** Submit the user's one-time role selection during onboarding. */
export async function selectRole(
  role: 'INDIVIDUAL' | 'PARTNER',
): Promise<void> {
  await authedJson<unknown>('/account/select-role', {
    method: 'POST',
    body: JSON.stringify({ role }),
  });
}

/** Persist profile changes; returns the fresh server-side profile. */
export async function updateProfile(
  patch: UpdateProfilePayload,
): Promise<SessionUser> {
  const data = await authedJson<any>('/auth/me', {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  return toSessionUser(data);
}
